import { useMemo } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { endOfDay, startOfDay } from "date-fns";
import { ConnectProto } from "@racoon/api";
import { useDriverOnShiftsQuery } from "~/shared/api/govDash/govDash.api";

interface UseDriverOnShiftsInRangeParams {
  districtOrganizationIds?: (string | number | bigint)[];
  serviceProviderIds?: (string | number | bigint)[];
  from: Date;
  to: Date;
  skip?: boolean;
}

/**
 * Shifts that put a truck on line. NOT_APPROVED (never approved by a dispatcher) and
 * CANCELLED (called off before any work) are left out, as is UNDEFINED.
 */
const COUNTED_SHIFT_STATUSES: ConnectProto.DriverOnShiftStatus[] = [
  ConnectProto.DriverOnShiftStatus.TRUCK_IDLE,
  ConnectProto.DriverOnShiftStatus.DRIVER_IDLE,
  ConnectProto.DriverOnShiftStatus.CONFIRMATION,
  ConnectProto.DriverOnShiftStatus.ENROUTE,
  ConnectProto.DriverOnShiftStatus.ON_SERVICE,
  ConnectProto.DriverOnShiftStatus.IDLE,
  ConnectProto.DriverOnShiftStatus.ACTIVE,
  ConnectProto.DriverOnShiftStatus.ENDED,
];

/** A truck with several shifts counts once. */
export const countDistinctTrucks = (shifts: ConnectProto.DriverOnShift[]) =>
  new Set(shifts.map((shift) => String(shift.truckId))).size;

/**
 * Driver shifts between the start of `from` and the end of `to`, per
 * GovDashService/DriverOnShifts, plus the number of distinct trucks on them.
 * `activeTrucks` is `undefined` while loading or on error.
 */
export function useDriverOnShiftsInRange({
  districtOrganizationIds = [],
  serviceProviderIds = [],
  from,
  to,
  skip = false,
}: UseDriverOnShiftsInRangeParams) {
  // RTK Query keys the cache on the serialized args, so a fresh object per render doesn't refetch.
  // skipToken rather than `{ skip }`: the args are not built at all when skipped, since
  // callers may pass a placeholder id (e.g. "all") that BigInt() cannot parse.
  const {
    currentData: shifts,
    isLoading,
    isFetching,
  } = useDriverOnShiftsQuery(
    skip
      ? skipToken
      : {
          filter: {
            districtOrganizationIds: districtOrganizationIds.map((id) => BigInt(id)),
            serviceProviderIds: serviceProviderIds.map((id) => BigInt(id)),
          },
          dateTimeFilter: {
            from: { seconds: BigInt(Math.floor(startOfDay(from).getTime() / 1000)) },
            to: { seconds: BigInt(Math.floor(endOfDay(to).getTime() / 1000)) },
            // Filter on the same start time countActiveTrucksByDay buckets by; the
            // endpoint's default column is created_at.
            fields: ["started_at", "tw_start_at"],
          },
          statuses: COUNTED_SHIFT_STATUSES,
        },
  );

  const activeTrucks = useMemo(() => (shifts ? countDistinctTrucks(shifts) : undefined), [shifts]);

  return { shifts, activeTrucks, isLoading, isFetching };
}
